/**
 * XRPL Connection Card Component
 *
 * Displays XRPL connection status and provides a reset button
 * for recovering from stuck/stale connections.
 */

import React, { useState } from 'react';
import { resetXrplConnection, getXrplStatus, type XrplResetResponse, type XrplStatusResponse } from '../../services/adminApi.js';

interface XrplConnectionCardProps {
  onResetComplete?: () => void;
}

export function XrplConnectionCard({ onResetComplete }: XrplConnectionCardProps) {
  const [status, setStatus] = useState<XrplStatusResponse['data'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<XrplResetResponse | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getXrplStatus();
      if (response.success) {
        setStatus(response.data);
      } else {
        setError('Failed to load XRPL status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load XRPL status');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setResetting(true);
      setError(null);
      setResetResult(null);
      setShowConfirmDialog(false);

      const response = await resetXrplConnection();
      setResetResult(response);

      if (response.success) {
        // Refresh status after successful reset
        await loadStatus();
        onResetComplete?.();
      } else {
        setError(response.message || 'Reset failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reset XRPL connection');
    } finally {
      setResetting(false);
    }
  };

  // Load status on mount
  React.useEffect(() => {
    loadStatus();
  }, []);

  const getConnectionStatusColor = () => {
    if (!status) return 'bg-gray-100 text-gray-600';
    if (!status.connected) return 'bg-red-100 text-red-800';
    if (!status.subscriptionHealthy) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getConnectionStatusText = () => {
    if (!status) return 'Unknown';
    if (!status.connected) return 'Disconnected';
    if (!status.subscriptionHealthy) return 'Connected (Stale)';
    return 'Connected (Healthy)';
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">XRPL Connection</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={loadStatus}
            disabled={loading || resetting}
            className="rounded-md bg-gray-100 px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button
            onClick={() => setShowConfirmDialog(true)}
            disabled={resetting || loading}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs sm:text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {resetting ? 'Resetting...' : 'Reset'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Reset Result Display */}
      {resetResult && (
        <div
          className={`mb-4 rounded-md p-3 text-sm ${
            resetResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          <div className="font-medium">{resetResult.success ? 'Reset Successful' : 'Reset Failed'}</div>
          <div className="mt-1">{resetResult.message}</div>
          {resetResult.data && (
            <div className="mt-2 text-xs">
              <div>Duration: {resetResult.data.resetDurationMs}ms</div>
              <div>Previous server: {resetResult.data.previousServer}</div>
              <div>New server: {resetResult.data.newServer}</div>
              <div>Subscriptions restored: {resetResult.data.subscriptionsRestored?.join(', ') || 'None'}</div>
            </div>
          )}
        </div>
      )}

      {/* Status Display */}
      {status ? (
        <div className="space-y-3 sm:space-y-4">
          {/* Connection Status Badge */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <span className="text-xs sm:text-sm font-medium text-gray-600">Status:</span>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold w-fit ${getConnectionStatusColor()}`}>
              {getConnectionStatusText()}
            </span>
          </div>

          {/* Connection Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
            <div className="bg-gray-50 rounded-md p-2 sm:p-3">
              <div className="text-xs font-medium text-gray-500">Server</div>
              <div className="text-xs sm:text-sm text-gray-900 truncate">{status.currentServer}</div>
            </div>
            <div className="bg-gray-50 rounded-md p-2 sm:p-3">
              <div className="text-xs font-medium text-gray-500">Last Ledger</div>
              <div className="text-xs sm:text-sm text-gray-900">
                {status.lastLedgerIndex?.toLocaleString() || 'N/A'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-md p-2 sm:p-3">
              <div className="text-xs font-medium text-gray-500">Last Event</div>
              <div className="text-xs sm:text-sm text-gray-900">
                {status.timeSinceLastLedgerFormatted || 'N/A'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-md p-2 sm:p-3">
              <div className="text-xs font-medium text-gray-500">Reconnect Attempts</div>
              <div className="text-xs sm:text-sm text-gray-900">
                {status.reconnectAttempts} / {status.maxReconnectAttempts}
              </div>
            </div>
          </div>

          {/* Warning for stale connections */}
          {status.connected && !status.subscriptionHealthy && (
            <div className="mt-3 rounded-md bg-yellow-50 p-3 text-xs sm:text-sm text-yellow-800">
              <strong>Warning:</strong> Connection is established but subscriptions appear stale.
              No ledger events have been received in {status.timeSinceLastLedgerFormatted || 'a while'}.
              Consider resetting the connection.
            </div>
          )}
        </div>
      ) : loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-6 w-32 rounded bg-gray-200"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="h-14 sm:h-16 rounded bg-gray-200"></div>
            <div className="h-14 sm:h-16 rounded bg-gray-200"></div>
            <div className="h-14 sm:h-16 rounded bg-gray-200"></div>
            <div className="h-14 sm:h-16 rounded bg-gray-200"></div>
          </div>
        </div>
      ) : (
        <div className="text-xs sm:text-sm text-gray-500">Failed to load status. Click Refresh to try again.</div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Reset XRPL Connection?</h3>
            <p className="mt-2 text-sm text-gray-600">
              This will completely tear down and rebuild the XRPL WebSocket connection,
              including all subscriptions. This operation takes a few seconds.
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Use this when ledgers appear stuck or the connection is stale.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Reset Connection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
